// V1.0.65: Modal lịch sử thay đổi dự án
// Nguồn dữ liệu: GET /api/projects/{id}/audit-log?page&pageSize
//   → PaginatedResponse<AuditLogDto> (Application/Common/Dtos.cs)
// Backend đã kiểm tra quyền xem project trước khi trả log.
import { useState, useEffect } from 'react';
import { Modal, Table, Tag, Tooltip, Empty, App as AntdApp } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { History } from 'lucide-react';
import dayjs from 'dayjs';
import { apiClient } from '@/services/apiClient';

interface Props {
  open: boolean;
  projectId: string;
  projectName: string;
  onClose: () => void;
}

interface AuditLog {
  id: number;
  timestamp: string;
  userId?: string | null;
  username?: string | null;
  ip?: string | null;
  action: string;
  entityType?: string | null;
  entityId?: string | null;
  entityDescription?: string | null;
  result: 'SUCCESS' | 'FAILURE';
  failureReason?: string | null;
}

interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

// Các action ProjectService ghi audit (xem ProjectService.LogAsync)
const ACTION_META: Record<string, { label: string; color: string }> = {
  CREATE_PROJECT: { label: 'Tạo dự án', color: 'green' },
  UPDATE_PROJECT: { label: 'Cập nhật dự án', color: 'cyan' },
  ARCHIVE_PROJECT: { label: 'Lưu trữ dự án', color: 'orange' },
  DELETE_PROJECT: { label: 'Xóa dự án', color: 'red' },
  ADD_MEMBER: { label: 'Thêm thành viên', color: 'blue' },
  REMOVE_MEMBER: { label: 'Gỡ thành viên', color: 'volcano' },
  UPDATE_MEMBER_ROLE: { label: 'Đổi vai trò', color: 'purple' },
};

function actionMeta(action: string) {
  return ACTION_META[action] ?? { label: action, color: 'default' };
}

export function ProjectAuditLogModal({ open, projectId, projectName, onClose }: Props) {
  const { message } = AntdApp.useApp();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const { data: res } = await apiClient.get<Paginated<AuditLog>>(
          `/projects/${projectId}/audit-log`,
          { params: { page, pageSize } },
        );
        if (cancelled) return;
        setData(res.items ?? []);
        setTotal(res.total ?? 0);
      } catch (e: any) {
        if (cancelled) return;
        message.error(e?.response?.data?.message ?? 'Không tải được lịch sử thay đổi');
        setData([]);
        setTotal(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, projectId, page, pageSize]);

  // Mở lại modal → về trang đầu
  useEffect(() => {
    if (open) setPage(1);
  }, [open, projectId]);

  const columns: ColumnsType<AuditLog> = [
    {
      title: 'Thời gian',
      dataIndex: 'timestamp',
      width: 150,
      render: (v: string) => (
        <Tooltip title={dayjs(v).format('DD/MM/YYYY HH:mm:ss')}>
          <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            {dayjs(v).format('DD/MM/YY HH:mm')}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Hành động',
      dataIndex: 'action',
      width: 160,
      render: (v: string) => {
        const meta = actionMeta(v);
        return <Tag color={meta.color} style={{ marginInlineEnd: 0 }}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Người thực hiện',
      dataIndex: 'username',
      width: 150,
      render: (v: string | null) => v || <span style={{ color: '#adb6c0' }}>—</span>,
    },
    {
      title: 'Chi tiết',
      dataIndex: 'entityDescription',
      render: (v: string | null, record) => (
        <div style={{ fontSize: 12 }}>
          <span>{v || '—'}</span>
          {record.result === 'FAILURE' && (
            <div style={{ color: '#d93025', marginTop: 2 }}>
              Thất bại: {record.failureReason || 'không rõ nguyên nhân'}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      width: 120,
      render: (v: string | null) => (
        <span style={{ fontSize: 11, color: '#7d8a98' }}>{v || '—'}</span>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnClose
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <History size={16} />
          Lịch sử thay đổi · {projectName}
        </span>
      }
    >
      <Table<AuditLog>
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={data}
        locale={{ emptyText: <Empty description="Chưa có lịch sử thay đổi" /> }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          showTotal: (t) => `${t} bản ghi`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
        }}
        scroll={{ y: 460 }}
      />
    </Modal>
  );
}

export default ProjectAuditLogModal;
